/**
 * safeStringify — render an arbitrary `unknown` value as a `string`.
 *
 * `JSON.stringify` is the natural reach for "show me what this value is",
 * but it has four sharp edges that bite cross-demo telemetry, witness
 * panels, and log surfaces:
 *
 *   1. It returns `undefined` for `undefined`, functions, and symbols.
 *   2. It throws on `BigInt` — `TypeError: Do not know how to serialize a BigInt`.
 *   3. It throws on circular references.
 *   4. It silently coerces `NaN` / `Infinity` to `null`.
 *
 * `String(value)` is the dual fallback, but it produces `[object Object]`
 * for any non-primitive — also useless.
 *
 * This helper picks a sensible rendering per JS type and never throws,
 * so consumers can treat its return as a plain `string` without further
 * guards. The output is *not* required to round-trip through `JSON.parse`;
 * it is meant for display, telemetry, and log lines.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

/** Sentinel returned when no other rendering path produces a string. */
export const UNRENDERABLE_SENTINEL = "[unrenderable]";

export interface SafeStringifyOptions {
  /**
   * Suffix appended to the numeric form of a BigInt. Defaults to `"n"`
   * so the output matches the JavaScript literal syntax (e.g. `42n`),
   * which is unambiguous in mixed-type telemetry. Pass `""` for the
   * bare numeric form.
   */
  readonly bigintSuffix?: "n" | "";
  /**
   * String used when every other rendering path fails (circular refs,
   * `toJSON` throws, etc.). Defaults to `UNRENDERABLE_SENTINEL`.
   */
  readonly fallback?: string;
}

/**
 * Render `value` as a string without throwing. See module docstring for
 * the rendering choices per type.
 *
 * Examples:
 *   safeStringify("a")             // '"a"'
 *   safeStringify(42)              // "42"
 *   safeStringify(true)            // "true"
 *   safeStringify(undefined)       // "undefined"
 *   safeStringify(null)            // "null"
 *   safeStringify(123n)            // "123n"
 *   safeStringify(Symbol("x"))     // "Symbol(x)"
 *   safeStringify(() => 1)         // "() => 1"
 *   safeStringify({ a: 1 })        // '{"a":1}'
 *   safeStringify(circular)        // "[unrenderable]"
 */
export function safeStringify(value: unknown, options: SafeStringifyOptions = {}): string {
  const { bigintSuffix = "n", fallback = UNRENDERABLE_SENTINEL } = options;

  if (typeof value === "string") return JSON.stringify(value);
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value === undefined || value === null) return String(value);
  if (typeof value === "bigint") return bigintToString(value, bigintSuffix);
  if (typeof value === "function" || typeof value === "symbol") return value.toString();

  // Object-like — try JSON, fall back on collapse or throw.
  try {
    const json = JSON.stringify(value);
    return typeof json === "string" ? json : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Render a `BigInt` as a string. The default suffix `"n"` matches the
 * JavaScript literal syntax (`42n`), so mixed-type telemetry that also
 * shows plain numbers stays unambiguous. Pass `""` for the bare numeric
 * form.
 *
 * `JSON.stringify(123n)` throws; this helper is the cross-demo answer.
 */
export function bigintToString(value: bigint, suffix: "n" | "" = "n"): string {
  return `${value.toString()}${suffix}`;
}
